import { supabase } from '../lib/supabase';

export const whatsappService = {
  /**
   * Send WhatsApp notification through universal webhook endpoint
   * @param {Object} params - Notification parameters
   * @param {string} params.phoneNumber - Recipient phone number
   * @param {string} params.message - Message content
   * @param {string} params.provider - Provider name (make, manychat, n8n, whatsapp_cloud)
   * @param {string} params.shipmentId - Associated shipment ID
   */
  async send({ phoneNumber, message, provider, shipmentId }) {
    try {
      // 1. Create notification record with pending status
      const { data: notification, error: notificationError } = await supabase?.from('whatsapp_notifications')?.insert({
          shipment_id: shipmentId,
          phone_number: phoneNumber,
          message: message,
          status: 'pending',
          provider: provider
        })?.select()?.single();

      if (notificationError) throw notificationError;

      // 2. Get webhook configuration for provider
      const { data: config, error: configError } = await supabase?.from('whatsapp_webhook_config')?.select('*')?.eq('provider', provider)?.eq('is_active', true)?.single();

      // 3. Prepare standard JSON payload
      const payload = {
        phone: phoneNumber,
        message: message,
        shipment_id: shipmentId,
        provider: provider,
        timestamp: new Date()?.toISOString(),
        webhook_source: 'ADT Tracking'
      };

      let success = false;
      let errorMessage = null;

      // 4. Send to provider webhook if configured
      if (config && config?.webhook_url) {
        try {
          const response = await fetch(config?.webhook_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(config?.api_key && { 'Authorization': `Bearer ${config?.api_key}` })
            },
            body: JSON.stringify(payload)
          });

          success = response?.ok;
          if (!success) {
            errorMessage = `Webhook failed: ${response?.status}`;
          }
        } catch (fetchError) {
          errorMessage = `Webhook error: ${fetchError?.message}`;
        }
      } else {
        // Mock success if no webhook configured (fallback mode)
        success = true;
      }

      // 5. Update notification status
      const { error: updateError } = await supabase?.from('whatsapp_notifications')?.update({
          status: success ? 'sent' : 'failed'
        })?.eq('id', notification?.id);

      if (updateError) throw updateError;

      return {
        success,
        notificationId: notification?.id,
        error: errorMessage
      };
    } catch (error) {
      console.error('WhatsApp send error:', error);
      throw error;
    }
  },

  /**
   * Get all notifications with optional filters
   */
  async getAll({ status = null, provider = null, shipmentId = null } = {}) {
    try {
      let query = supabase?.from('whatsapp_notifications')?.select(`
          *,
          shipments (
            reference,
            receiver_name,
            current_status
          )
        `)?.order('created_at', { ascending: false });

      if (status) {
        query = query?.eq('status', status);
      }
      if (provider) {
        query = query?.eq('provider', provider);
      }
      if (shipmentId) {
        query = query?.eq('shipment_id', shipmentId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(notification => ({
        id: notification?.id,
        shipmentId: notification?.shipment_id,
        phoneNumber: notification?.phone_number,
        message: notification?.message,
        status: notification?.status,
        provider: notification?.provider,
        createdAt: notification?.created_at,
        updatedAt: notification?.updated_at,
        shipment: notification?.shipments ? {
          reference: notification?.shipments?.reference,
          receiverName: notification?.shipments?.receiver_name,
          currentStatus: notification?.shipments?.current_status
        } : null
      }));
    } catch (error) {
      console.error('Get notifications error:', error);
      throw error;
    }
  },

  /**
   * Resend a failed notification
   */
  async resend(notificationId) {
    try {
      // Get original notification
      const { data: notification, error: fetchError } = await supabase?.from('whatsapp_notifications')?.select('*')?.eq('id', notificationId)?.single();

      if (fetchError) throw fetchError;

      // Resend using original parameters
      return await this.send({
        phoneNumber: notification?.phone_number,
        message: notification?.message,
        provider: notification?.provider,
        shipmentId: notification?.shipment_id
      });
    } catch (error) {
      console.error('Resend notification error:', error);
      throw error;
    }
  },

  /**
   * Delete a notification
   */
  async delete(notificationId) {
    try {
      const { error } = await supabase?.from('whatsapp_notifications')?.delete()?.eq('id', notificationId);

      if (error) throw error;
    } catch (error) {
      console.error('Delete notification error:', error);
      throw error;
    }
  },

  /**
   * Generate status-based message
   */
  async generateMessage(status, reference, receiverName) {
    try {
      const { data, error } = await supabase?.rpc('generate_whatsapp_message', {
          p_status: status,
          p_reference: reference,
          p_receiver_name: receiverName
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Generate message error:', error);
      throw error;
    }
  },

  /**
   * Get webhook configurations
   */
  async getWebhookConfigs() {
    try {
      const { data, error } = await supabase?.from('whatsapp_webhook_config')?.select('*')?.order('provider');

      if (error) throw error;

      return data?.map(config => ({
        id: config?.id,
        provider: config?.provider,
        webhookUrl: config?.webhook_url,
        apiKey: config?.api_key,
        isActive: config?.is_active,
        createdAt: config?.created_at,
        updatedAt: config?.updated_at
      }));
    } catch (error) {
      console.error('Get webhook configs error:', error);
      throw error;
    }
  },

  /**
   * Update webhook configuration
   */
  async updateWebhookConfig(provider, { webhookUrl, apiKey, isActive }) {
    try {
      const { data, error } = await supabase?.from('whatsapp_webhook_config')?.update({
          webhook_url: webhookUrl,
          api_key: apiKey,
          is_active: isActive
        })?.eq('provider', provider)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        provider: data?.provider,
        webhookUrl: data?.webhook_url,
        apiKey: data?.api_key,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Update webhook config error:', error);
      throw error;
    }
  },

  /**
   * Test webhook connection
   */
  async testWebhook(provider) {
    try {
      const { data: config, error: configError } = await supabase?.from('whatsapp_webhook_config')?.select('*')?.eq('provider', provider)?.single();

      if (configError) throw configError;

      if (!config?.webhook_url) {
        throw new Error('Webhook URL not configured');
      }

      const testPayload = {
        phone: '+33600000000',
        message: 'Test message from ADT Tracking',
        test: true,
        timestamp: new Date()?.toISOString(),
        webhook_source: 'ADT Tracking'
      };

      const response = await fetch(config?.webhook_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(config?.api_key && { 'Authorization': `Bearer ${config?.api_key}` })
        },
        body: JSON.stringify(testPayload)
      });

      return {
        success: response?.ok,
        status: response?.status,
        statusText: response?.statusText
      };
    } catch (error) {
      console.error('Test webhook error:', error);
      throw error;
    }
  }
};